/* ═══════════════════════════════════════════════════════════════════════════
   EOD SUMMARY REPORT — ClickHouse migration interfaces
   Endpoint: GET {REPORTS_BASE_URL}/sales/live/v2/eodSummaryReport

   Replaces the legacy paginated `/eod/getEODReports` call behind the EOD
   Reconciliation report's "Summary Report" table. Like the legacy endpoint it
   is server-paginated and server-sorted (`page` / `size` / `sortBy` /
   `sortAsc`), and it returns a pre-computed `totals` object alongside the page
   of rows. `page` is ONE-based on the wire; the table keeps a zero-based page
   in state and adds 1 when dispatching, matching every other paginated report.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Query params. `startDate` / `endDate` are ISO `YYYY-MM-DD`; omit both for the live day. */
export interface EodSummaryReportPayload {
  locationId: string;
  startDate?: string;
  endDate?: string;
  /** ONE-based page index. Defaults to 1 when omitted. */
  page?: number;
  /** Rows per page. Defaults to 10 on desktop and 5 on mobile — see EOD_PAGE_SIZE_*. */
  size?: number;
  /** Sort column — a response field name, e.g. `businessDate` / `directSales`. */
  sortBy?: string;
  /** Sort direction. Defaults to `true` (ascending) when omitted. */
  sortAsc?: boolean;
  /** Optional bearer override — resolved from stored CREDENTIALS when absent. */
  token?: string | null;
}

/** Defaults the table starts from — page 1, ascending, device-dependent page size. */
export const EOD_SUMMARY_DEFAULT_PAGE = 1;
export const EOD_SUMMARY_DEFAULT_SORT_ASC = true;
export const EOD_PAGE_SIZE_DESKTOP = 10;
export const EOD_PAGE_SIZE_MOBILE = 5;

/**
 * One business date. `businessDate` is null on the `totals` object only.
 * Every money field is a plain number (never a string, never null) in the
 * observed payload, but each is read through `Number(... ?? 0)` downstream.
 */
export interface EodSummaryReportRow {
  /** ISO `YYYY-MM-DD`; null on the totals row. */
  businessDate: string | null;
  /** First-party (Maghil) sales — the "TOTAL SALES" column of the Direct Sales view. */
  directSales: number;
  /** Marketplace sales — the "TOTAL SALES" column of the 3rd Party Sales view. */
  thirdPartySales: number;
  /** directSales + thirdPartySales. Not rendered today; kept for parity with the payload. */
  totalSales: number;
  /** Card / credit tender — the "CREDIT SALES (EXPECTED FUNDS)" column. */
  creditSales: number;
  cashSales: number;
  /** Everything that is neither cash nor credit — the "ALTERNATIVE PAYMENTS" column. */
  otherSales: number;
  doordashSales: number;
  uberEatsSales: number;
  grubhubSales: number;
  /**
   * Funds actually settled to the merchant — the "Processed Funds" column of the
   * Funds table, paired against `creditSales` ("Expected Funds").
   *
   * Optional because the endpoint's first published payload carried only the
   * nine sales fields above; the Funds table reads it through `Number(... ?? 0)`
   * so a body without it renders 0 rather than throwing.
   */
  maghilFunding?: number;
  /**
   * REF-4 — real per-tender refund totals, added to the payload alongside the sales
   * fields above. Optional for the same reason as `maghilFunding`: earlier payloads did
   * not carry them, so every read goes through `Number(... ?? 0)`.
   *   refunded          — total cash returned across all tenders for the row/period.
   *   cashSalesRefund    — nets against `cashSales` ("Cash Sales").
   *   creditSalesRefund  — nets against `creditSales` ("Credit Sales (Expected Funds)").
   *   otherSalesRefund   — nets against `otherSales` ("Alternative Payments").
   */
  refunded?: number;
  cashSalesRefund?: number;
  creditSalesRefund?: number;
  otherSalesRefund?: number;
}

/**
 * Full response body. `totals` is pre-aggregated by the backend across the WHOLE
 * range (not just the returned page) — never recomputed here.
 *
 * The pagination metadata is optional because the sample payload carried only
 * `rows` + `totals`; the saga reads `totalElements` / `totalPages` when the
 * backend sends them and derives a safe fallback when it does not.
 */
export interface EodSummaryReportResponse {
  rows: EodSummaryReportRow[];
  totals: EodSummaryReportRow | null;
  /** Row count across the whole range. Aliases accepted: `totalRecords`, `totalCount`. */
  totalElements?: number;
  totalPages?: number;
  totalRecords?: number;
  totalCount?: number;
}

/** What the saga puts in the store — pagination metadata always resolved to a number. */
export interface EodSummaryReportNormalised {
  rows: EodSummaryReportRow[];
  totals: EodSummaryReportRow | null;
  totalElements: number;
  totalPages: number;
}

/**
 * Row shape the Summary Report table renders, mapped from `EodSummaryReportRow`.
 * Column keys are unchanged from the legacy `/eod/getEODReports` payload so the
 * table headers, sort keys, cell formatter and export builders keep working.
 */
export interface EodSummaryTableRow {
  /** `businessDate`, or the literal `'TOTALS'` for the pinned totals row. */
  date: string;
  // ── Direct Sales view ──
  maghilTotalSale: number;
  cashSale: number;
  otherSale: number;
  cardSale: number;
  // ── 3rd Party Sales view ──
  thirdPartyTotalSale: number;
  ubereats: number;
  grubhub: number;
  doordash: number;
  // REF-4 — real per-tender refund amounts, from `cashSalesRefund` / `otherSalesRefund` /
  // `creditSalesRefund`. Direct Sales view only; nets Cash Sales / Alternative Payments /
  // Credit Sales and drives their "− $X refunded" caption.
  cashSaleRefund: number;
  otherSaleRefund: number;
  cardSaleRefund: number;
}

/* ═══════════════════════════════════════════════════════════════════════════
   FUNDS TABLE — same `sales/live/v2/eodSummaryReport` endpoint, three columns
   (Date / Expected Funds / Processed Funds) drawn from `businessDate`,
   `creditSales` and `maghilFunding`. It keeps its OWN page / size / sort state
   and therefore its own request and its own slice of the store, even though it
   shares the endpoint with the Summary Report table above.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Sort column the Funds table sends — its only sortable header is Date. */
export const EOD_FUNDS_SORT_BY = 'businessDate';

/**
 * Row shape the Funds table renders. Field names are unchanged from the legacy
 * `/eod/getEODReports` mapping so the row renderer, the mismatch highlight and
 * the export builder keep working.
 */
export interface EodFundsTableRow {
  /** `businessDate`, or the literal `'TOTALS'` for the pinned totals row. */
  date: string;
  /** `creditSales` — Expected Funds. */
  expected: number;
  /** `maghilFunding` — Processed Funds. */
  processed: number;
}
